import { Box, Typography } from "@mui/material";
import pluralize from "pluralize";
import { useContext, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { Handle, Position } from "reactflow";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import CustomToolTip from "../../../../components/CustomTooltip";
import CustomTooltipBorder from "../../../../components/CustomTooltipBorder";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import icon_mapping from "../../../../icons/IconMapping";
import SvgToIcon from "../../../../icons/SvgToIcon";
import {
  AttackerTooltip,
  ProtectionLevelText,
} from "../../../../utils/constants";
import {
  detectionIcons,
  icons,
  severities,
} from "../../../stack/environment/icons/iconExport";
import BadgeOverlap from "./BadgeOverlap";
import IconAboveNode from "./IconAboveNode";
import { executionStepDescriptorByAssetType } from "./executionSteps";
import CriticalAssetNodeTooltip from "./nodeTooltip/CriticalAssetNodeTooltip";
import ScenarioSetNodeToolTip from "./nodeTooltip/ScenarioSetNodeToolTip";

// TODO split to components for each data type
function ScenarioSetNode({ data, isConnectable }) {
  const colors = useColors();
  const nodeDefaultSize = "80px";
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [Icon, setIcon] = useState(icon_mapping["default"]);
  const [isHovered, setIsHovered] = useState(false);
  const [toolTipStartTimer, setToolTipStartTimer] = useState(null);
  const { trackEvent } = useAPI();
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);

  const handleTooltipOpen = () => {
    // Start the timer when the tooltip opens
    setToolTipStartTimer(new Date());
  };

  const handleTooltipClose = (name) => {
    if (toolTipStartTimer) {
      // Calculate the time the tooltip was open
      const timeHovered = new Date().getTime() - toolTipStartTimer.getTime();
      // Track the time the tooltip was open in seconds
      trackEvent(
        "Scenario Modal tooltip - " +
          name +
          " viewed for : " +
          timeHovered / 1000 +
          "s"
      );
      // Reset the timer
      setToolTipStartTimer(null);
    }
  };

  const colors_mapping = {
    [ProtectionLevelText.AT_RISK]: {
      color: colors.error_main,
      backgroundColor: colors.error_soft,
      colorOpacity: colors.error_32percent,
    },
    [ProtectionLevelText.MISSING_CONTROLS]: {
      color: colors.error_main,
      backgroundColor: colors.error_soft,
      colorOpacity: colors.error_32percent,
    },
    [ProtectionLevelText.DETECTION_ONLY]: {
      color: colors.warning_dark,
      backgroundColor: colors.warning_orange_soft,
      colorOpacity: colors.warning_48percent,
    },
    [ProtectionLevelText.PARTIAL_PROTECTION]: {
      color: colors.warning_main,
      backgroundColor: colors.warning_yellow_soft,
      colorOpacity: colors.warning_20percent,
    },
    [ProtectionLevelText.OPTIMAL_PROTECTION]: {
      color: colors.success_main,
      backgroundColor: colors.success_soft,
      colorOpacity: colors.success_20percent,
    },
    [ProtectionLevelText.RISK_ACCEPTED]: {
      color: colors.grey_600,
      backgroundColor: colors.grey_4percent,
      colorOpacity: colors.grey_4percent,
    },
  };

  const pulseAnimation = {
    "@keyframes pulse": {
      "0%": {
        transform: "scale(1.2)",
      },
      "50%": {
        transform: "scale(1)",
      },
      "100%": {
        transform: "scale(1.2)",
      },
    },
    animation: "pulse 1.5s infinite",
  };

  useEffect(() => {
    let icon_descriptor;

    switch (data.type) {
      case "location":
        icon_descriptor = data.nodeValue;
        break;
      case "asset":
        icon_descriptor = data.nodeValue.type;
        break;
      case "execution":
        const value = data.nodeValue.name;
        icon_descriptor = executionStepDescriptorByAssetType[value];
        break;
      case "step":
        icon_descriptor =
          data.nodeValue.is_virtual || !data.nodeValue.vector
            ? "Attacker"
            : data.nodeValue.vector;
        break;
      default:
        icon_descriptor = "default";
    }

    if (icon_mapping[icon_descriptor]) {
      setIcon(() => icon_mapping[icon_descriptor]);
    }
    // otherwise fallback to the default set earlier
  }, [data]);

  const handleMoreInfoClick = (step_id) => {
    const params = new URLSearchParams(location.search);
    params.set("step_id", step_id);
    setSearchParams(params.toString());

    // Filter irrelevant entities for vulnerability step node
    if (data.nodeValue.cwe_id && data?.entity_type_filter) {
      params.set("entity_type_filter", data.entity_type_filter);
      setSearchParams(params.toString());
    }
  };
  const handleEntityChipStepClick = (step_id, entity_type) => {
    const params = new URLSearchParams(location.search);
    params.set("step_id", step_id);
    setSearchParams(params.toString());

    // Filter irrelevant entities for vulnerability step node
    if (data.nodeValue.cwe_id && data?.entity_type_filter) {
      params.set("entity_type_filter", data.entity_type_filter);
      setSearchParams(params.toString());
    }
    params.set("entitiesDrawer", entity_type);
    setSearchParams(params.toString());
  };

  const createStepComponent = (data) => {
    return (
      <CustomToolTip
        title={
          <ScenarioSetNodeToolTip
            item={data}
            handleMoreInfoClick={handleMoreInfoClick}
            handleEntityChipStepClick={handleEntityChipStepClick}
            Icon={Icon}
          />
        }
        onClose={() => handleTooltipClose("Step " + data.nodeValue.step_id)}
        onOpen={handleTooltipOpen}
        placement="right"
        PopperProps={{
          modifiers: [
            {
              name: "preventOverflow",
              options: {
                boundary: document.body,
                altAxis: true,
                padding: 64,
              },
            },
            {
              name: "flip",
              options: {
                fallbackPlacements: ["left", "top", "bottom"],
              },
            },
          ],
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {detectionsActive && data.nodeValue.is_detected === true ? (
            <Box sx={{ top: "-8px", position: "absolute" }}>
              <SvgToIcon
                width={"16px"}
                IconLogo={detectionIcons.DetectionRed}
                style={pulseAnimation}
              />
            </Box>
          ) : (
            data.nodeValue.impact === "High" && (
              <IconAboveNode
                icon={severities.SeverityBlackIcon}
              ></IconAboveNode>
            )
          )}

          {data.nodeValue.cwe_id ? (
            // Vulnerability step node
            <Box>
              {data.nodeValue.has_exploit_in_the_wild === true && (
                <IconAboveNode
                  icon={icons.exploitInTheWildIcon}
                ></IconAboveNode>
              )}
              <Box
                sx={{
                  display: "flex",
                  height: "50px",
                  width: "50px",
                  flexDirection: "column",
                  alignItems: "center",
                  borderRadius: "50%",
                  backgroundColor: colors.error_soft_darker,
                  border: `2px solid ${
                    colors.error_dark ?? colors.common_black
                  }`,
                  justifyContent: "center",
                  //     boxShadow: `0px 0px 0px 2px ${colors.error_box_shadow_color}`,
                }}
              >
                <SvgToIcon
                  IconLogo={Icon}
                  width={"40px"}
                  style={{ color: "transparent" }}
                />
              </Box>
            </Box>
          ) : (
            // Control step node
            <>
              <BadgeOverlap
                items={data.nodeValue.mitigating_controls_tools_set}
                impact={data.nodeValue.impact}
                key={data.nodeValue.id + "badge"}
                size={"big"}
              >
                <Box
                  sx={{
                    display: "flex",
                    height: "56px",
                    width: "56px",
                    flexDirection: "column",
                    alignItems: "center",
                    borderRadius: "50%",
                    backgroundColor:
                      colors_mapping[data.nodeValue.protection_level]
                        ?.backgroundColor,
                    border: `2px solid ${
                      colors_mapping[data.nodeValue.protection_level]?.color ??
                      colors.common_black
                    }`,
                    justifyContent: "center",
                    // boxShadow:
                    //   "0px 0px 0px 3px " +
                    //   colors_mapping[data.nodeValue.protection_level]
                    //     ?.backgroundColor,
                  }}
                >
                  <SvgToIcon
                    IconLogo={Icon}
                    width={"56px"}
                    style={{ color: "transparent" }}
                  />
                </Box>
              </BadgeOverlap>
            </>
          )}
        </Box>
      </CustomToolTip>
    );
  };

  const createPhaseComponent = (data) => {
    return (
      <CustomToolTip
        title={pluralize(
          "Attack Path",
          data.nodeValue.number_of_phase_combos,
          true
        )}
      >
        <Box
          sx={{
            display: "flex",
            border: `1px solid ` + colors.primary_dark,
            flexDirection: "column",
            alignItems: "center",
            borderRadius: "50%",
            justifyContent: "center",
            width: "10px",
            height: "10px",
            padding: "10px",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: "8px", color: colors.primary_dark }}
          >
            {data.nodeValue.number_of_phase_combos}
          </Typography>
        </Box>
      </CustomToolTip>
    );
  };

  const createLocationComponent = (data) => {
    const containerStyle = {
      position: "relative",
      overflow: "visible",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: data.graphSource === "scenarioSetGraph" ? nodeDefaultSize : 32,
      height:
        data.graphSource === "scenarioSetGraph"
          ? `${parseInt(nodeDefaultSize) * 0.9}px`
          : "28px",
      zIndex: 0,
    };

    return (
      <CustomToolTip
        title={data.graphSource === "scenarioSetGraph" ? data.nodeValue : ""}
      >
        <Box sx={containerStyle}>
          <SvgToIcon IconLogo={icons.LocationHexagon} width={"100%"} />
          <SvgToIcon
            IconLogo={Icon}
            width={data.graphSource === "scenarioSetGraph" ? "40px" : "16px"}
            style={{
              color: "transparent",
              position: "absolute",
              top: data.graphSource === "scenarioSetGraph" ? 16 : 6,
              left: data.graphSource === "scenarioSetGraph" ? 21 : 8.5,
            }}
          />
        </Box>
      </CustomToolTip>
    );
  };

  const createAssetComponent = (data) => {
    const containerStyle = {
      position: "relative",
      overflow: "visible",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width:
        data.graphSource === "scenarioSetGraph"
          ? `${parseInt(nodeDefaultSize) + 10}px`
          : "45px",
      height:
        data.graphSource === "scenarioSetGraph"
          ? `${parseInt(nodeDefaultSize) * 0.9}px`
          : "35px",
    };

    const AttackerIconStyle = {
      position: "absolute",
      top: "-30px",
      height: "30px",
      width: "30px",
    };

    return (
      <Box>
        <CustomTooltipBorder
          title={
            data.graphSource === "scenarioSetGraph"
              ? AttackerTooltip[data.nodeValue.type]
              : ""
          }
          borderColor={colors.error_dark}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 10],
                  },
                },
              ],
            },
          }}
          PopperProps={{
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  boundary: document.body,
                  altAxis: true,
                  padding: 64,
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["left", "top", "bottom"],
                },
              },
            ],
          }}
        >
          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
            }}
          >
            {data.graphSource === "scenarioSetGraph" &&
              !data.isScenarioMitigated && (
                <SvgToIcon
                  IconLogo={icons.AssetAttackerIcon}
                  style={AttackerIconStyle}
                />
              )}
          </Box>
        </CustomTooltipBorder>
        <BadgeOverlap
          items={["asset"]}
          impact={data.nodeValue.impact}
          key={data.nodeValue.id + "badge"}
          size={data.graphSource === "scenarioSetGraph" ? "standard" : "small"}
          location={"mr"}
        >
          <CustomTooltipBorder
            title={
              data.graphSource === "scenarioSetGraph" ? (
                <CriticalAssetNodeTooltip item={data} Icon={Icon} />
              ) : (
                ""
              )
            }
            borderColor={colors.primary_main}
          >
            <Box sx={containerStyle}>
              <SvgToIcon IconLogo={icons.AssetHexagon} width={"100%"} />
              <SvgToIcon
                IconLogo={Icon}
                width={
                  data.graphSource === "scenarioSetGraph" ? "40px" : "20px"
                }
                style={{
                  color: colors.primary_main,
                  position: "absolute",
                  top: data.graphSource === "scenarioSetGraph" ? 16 : 8,
                  left: data.graphSource === "scenarioSetGraph" ? 25 : 12,
                }}
              />
            </Box>
          </CustomTooltipBorder>
        </BadgeOverlap>
      </Box>
    );
  };

  const createExecutionComponent = (data) => {
    return (
      <>
        {data.nodeValue?.description.Description ? (
          <CustomTooltipBorder
            title={
              <ScenarioSetNodeToolTip
                item={data}
                handleMoreInfoClick={handleMoreInfoClick}
                handleEntityChipStepClick={handleEntityChipStepClick}
                Icon={Icon}
              />
            }
            onClose={() => handleTooltipClose("Execution Step")}
            onOpen={handleTooltipOpen}
            borderColor={colors.error_main}
            placement="right"
            PopperProps={{
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    boundary: document.body,
                    altAxis: true,
                    padding: 64,
                  },
                },
                {
                  name: "flip",
                  options: {
                    fallbackPlacements: ["left", "top", "bottom"],
                  },
                },
              ],
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: nodeDefaultSize,
                height: nodeDefaultSize,
                justifyContent: "center",
              }}
            >
              <SvgToIcon IconLogo={icons.executionIcon} width={"100px"} />
            </Box>
          </CustomTooltipBorder>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: nodeDefaultSize,
              height: nodeDefaultSize,
              justifyContent: "center",
            }}
          >
            <SvgToIcon IconLogo={icons.executionIcon} width={"100px"} />
          </Box>
        )}
      </>
    );
  };

  const createMessageComponent = (data) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: nodeDefaultSize,
          height: nodeDefaultSize,
          justifyContent: "center",
        }}
      >
        <Typography variant="caption" sx={{ fontSize: "8px" }}>
          No scenarios match your filters. Please adjust and try again.
        </Typography>
      </Box>
    );
  };

  const renderHandle = (type, position, id) => (
    <Handle
      type={type}
      position={position}
      style={{ visibility: "hidden" }}
      id={id}
      isConnectable={isConnectable}
    />
  );

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        height: "80px",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        justifyContent: "center",
        alignItems: "center",
        transition: "transform 0.3s",
        transform: isHovered ? "scale(1.04)" : "scale(1)",
        justifyItems: "center",
        gap: 8,
        filter:
          data.isSubsequentNode && data.type !== "asset"
            ? "grayscale(100%) blur(0.5px)"
            : "none",
        opacity: data.isSubsequentNode && data.type !== "asset" ? 0.5 : 1,
      }}
    >
      {data.type === "asset" && createAssetComponent(data)}
      {data.type === "execution" && createExecutionComponent(data)}
      {data.type === "phase" && createPhaseComponent(data)}
      {data.type === "step" && createStepComponent(data)}
      {data.type === "location" && createLocationComponent(data)}
      {data.type === "message" && createMessageComponent(data)}
      {data.nodeLocation !== "end" &&
        renderHandle("source", Position.Right, "to")}
      {data.nodeLocation !== "start" &&
        renderHandle("target", Position.Left, "from")}
      {data.graphSource !== "scenarioSetGraph" &&
        (data.type === "location" || data.type === "asset") && (
          <Box
            sx={{
              position: "absolute",
              bottom: "10px",
              left: "50%",
              transform: "translateX(-50%)",
              borderRadius: "10px",
              backgroundColor: colors.common_white,
              boxShadow: "0px 0px 3px 1px " + colors.grey_300,
              justifyContent: "center",
              display: "flex",
              padding: "0 2px",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color:
                  data.type === "location"
                    ? colors.grey_dark
                    : colors.primary_dark,
                fontSize: "6px",
              }}
            >
              {data.type === "location" ? data.nodeValue : data.nodeValue.name}
            </Typography>
          </Box>
        )}
    </div>
  );
}

export default ScenarioSetNode;
